import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { startWith, Subject, Subscription } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import { selectLanguageData } from 'src/app/state/selectors';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { FormGroup } from '@angular/forms';
import { RxFormBuilder, RxwebValidators } from '@rxweb/reactive-form-validators';
import { capitalizeFirst, toLowerCase } from 'src/utils/string-utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-supplier',
  templateUrl: './supplier.component.html',
  styleUrls: ['./supplier.component.css'],
})
export class SupplierComponent implements OnInit, OnDestroy {
  // 🔽 DataTable setup
  dtOptions: DataTables.Settings = {};
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;


  // 🔽 Supplier list
  supplierList: any[] = [];

  // 🔽 Language support
  public lang: any = {};
  languageData$: any;
  selectedLanguage: string = '';

  // 🔽 UI toggles
  showAddForm = true;
  showGroupContacts = false;
  showCreateSupplier = false;

  // 🔽 Filtering
  selectedPlan: string = '';
  selectedAction: string = '';
  selectedSupplier: string = 'All';
  selectedRegion: string = '';

  // 🔽 Static options
  suppliers: string[] = ['All', 'Green Shield', 'Equitable', 'Cowan'];
  actions: string[] = ['Edit Catalog', 'Create New Catalog', 'Display Catalog'];
  plans: string[] = [
    'Health & Dental',
    'Wellness',
    'Travel',
    'Private Health',
    'TeleMedicine',
    'Life & CI',
    'Bundle 1',
    'Bundle',
  ];

  constructor(
    private store: Store<{ languageData: [] }>,
    private http: HttpClient, private formBuilder: RxFormBuilder
  ) {
    this.languageData$ = store.select(selectLanguageData);
  }

  // 🔽 Toggle Create Supplier UI
  toggleCreateSupplier(show: boolean) {
    this.showCreateSupplier = show;
  }

  // 🔽 Handle Region Change
  onRegionChange() {
    console.log('Selected Region: ', this.selectedRegion);
  }

  // 🔽 Fetch supplier data
  existingSuppliers: any = [];

  getSuppliers(): void {
    const url = `${environment.apiUrl}/api/ap/suppliers`; // Correct string interpolation

    this.http.get(url).subscribe({
      next: (response: any) => {
        this.supplierList = response;
        this.existingSuppliers = response.map((item) => ({
          id: item.id,
          name: item.supplierName
        }));
        console.log("fetched all suppliers:", response);

        if (this.dtElement && this.dtElement.dtInstance) {
          this.rerender(); // Reinitialize
        } else {
          this.dtTrigger.next(null); // First init
        }
      },
      error: (err) => {
        console.error('Failed to fetch suppliers', err);
      },
    });
  }


  // 🔁 Destroy and re-render DataTable
  rerender(): void {
    if (this.dtElement && this.dtElement.dtInstance) {
      this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
        dtInstance.destroy();
        this.dtTrigger.next(null);
      });
    } else {
      console.warn('DataTable not initialized yet.');
    }
  }

  refreshParentData(): void {
    this.getSuppliers();
  }

  // input number field
  allowOnlyNumbers(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    const isDigit = /^[0-9]$/.test(event.key);
    if (!isDigit) {
      event.preventDefault(); // Block anything that's not a digit
    }
  }


  // delete supplier
  showDeleteSupplierConfirm = false;
  showDeleteProductConfirm = false;
  selectedSingleSupplier = { id: '', name: '' };
  productIDForDelete: string = null;
  productNameForDelete: string = '';

  triggerDeleteSupplierModal(supplier: { id: string, name: string }) {
    this.selectedSingleSupplier = supplier;
    this.showDeleteSupplierConfirm = true;
    console.log("delete supplier :", supplier)
  }

  triggerDeleteProductModal(productId: string, productName: string): void {
    this.productIDForDelete = productId;
    this.productNameForDelete = productName;
    this.showDeleteProductConfirm = true;
    console.log("Selected for deletion -> ID:", productId, "Name:", productName);
  }


  onConfirmDeleteSupplier(supplier: { id: string; name: string }): void {
    const deleteUrl = `${environment.apiUrl}/api/ap/suppliers/${supplier.id}`;


    this.http.delete(deleteUrl).subscribe({
      next: () => {
        // window.location.reload();
        console.log(`Supplier ${supplier.name} deleted successfully`);
        this.getSuppliers()
        // Remove supplier from the local list
        this.supplierList = this.supplierList.filter(
          (s: any) => s.supplierId !== supplier.id
        );

        // Optionally re-render table
        if (this.dtElement && this.dtElement.dtInstance) {
          this.rerender();
        }

        this.showDeleteSupplierConfirm = false;
      },
      error: (err) => {
        console.error('Failed to delete supplier:', err);
        alert('Error deleting supplier.');
      },
    });
  }
  showConfirmationContactDelete: boolean = false;
  contactId: number = null;
  supplierId: number = null;
  contactNameForDelete: string = null;
  onConfirmDeleteContact(contactId: number, supplierId: number, contactName: string): void {
    this.contactNameForDelete = contactName
    this.contactId = contactId
    this.supplierId = supplierId
    this.showConfirmationContactDelete = true;
  }
  deleteContact() {
    const supplierId = this.supplierId;
    const contactId = this.contactId;
    const deleteUrl = `${environment.apiUrl}/api/ap/suppliers/delete-contact/${supplierId}/${contactId}`;
    this.http.delete(deleteUrl).subscribe({
      next: () => {
        this.showConfirmationContactDelete = false;
        this.showContactModal = false;
        // Refresh supplier list or update UI accordingly
        this.getSuppliers(); // If you're showing a table/list of suppliers
        console.log(`Deleted contact ${this.contactId} from supplier ${this.supplierId}`);
        // this.showDeleteContactConfirm = false;
      },
      error: (err) => {
        console.error('Failed to delete contact:', err);
        alert('Error deleting contact.');
      },
    });
  }
  onConfirmDeleteProduct(productId: string, supplierId: number): void {
    const deleteUrl = `${environment.apiUrl}/api/ap/suppliers/delete-product/${productId}/${supplierId}`;
    this.http.delete(deleteUrl).subscribe({
      next: () => {
        // window.location.reload();
        this.getSuppliers(); // if you're showing suppliers table
        console.log(`Deleted product ${productId} from supplier ${supplierId}`);
        this.showDeleteProductConfirm = false;
        this.showProductModal = false;
        // Optional: Refresh product list after deletion
      },
      error: (err) => {
        console.error('Failed to delete product:', err);
        alert('Error deleting product.');
      },
    });
  }
  // ******************************** Contact Information ***************************************
  contactForm!: FormGroup;
  showOther: boolean = false;
  showAddContactModal: boolean = false;
  submitted = false;
  showContactEditModal: boolean = false;

  contact_other: string = '';
  firstName: string = '';
  lastName: string = '';
  email: string = '';
  phone: string = '';
  role: string = '';
  selectedIndex: Number = null;

  showContactModal: boolean = false;
  showDeleteConfirm: boolean = false;
  contacts: any[] = [];

  //roles
  roles: string[] = [
    'Accountant',
    'Key Account Manager',
    'Product Manager',
    'Underwriter',
    'Other'
  ];

  onRoleChange(event: any) {
    const selectedValue = event.target.value;
    this.showOther = selectedValue === 'Other';

    const otherControl = this.contactForm.get('contact_other');

    if (this.showOther) {
      // Apply required validator
      otherControl?.setValidators(RxwebValidators.required());
    } else {
      // Remove validator and clear value
      otherControl?.clearValidators();
      otherControl?.setValue('');
      this.contact_other = '';
    }

    otherControl?.updateValueAndValidity();
  }

  allowOnlyNumbersForPhoneNumber(event: KeyboardEvent, inputValue: string) {
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];

    // Allow control keys
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Prevent if not a number
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }

    // Prevent if already 11 digits
    if (inputValue.length >= 10) {
      event.preventDefault();
    }
  }

  //save contact
  onSaveContact() {
    this.submitted = true;
    const contact = {
      firstName: capitalizeFirst(this.firstName),
      lastName: capitalizeFirst(this.lastName),
      email: toLowerCase(this.email),
      phone: this.phone,
      role: this.contact_other.length > 0 ? this.contact_other : capitalizeFirst(this.role)
    };
    if (this.contactForm.valid) {
      const maxId = this.contacts.length > 0 ? Math.max(...this.contacts.map(item => item.id)) : 0;
      // Push new item with next id
      this.contacts.push({ id: maxId + 1, ...contact });
      const newContact = { id: maxId + 1, ...contact }
      // API CALL for adding New Contact
      const supplierId = this.selectedSupplierId
      const url = `${environment.apiUrl}/api/ap/suppliers/add-contact/${supplierId}`;

      this.http.post(url, { contactData: newContact }).subscribe({
        next: (res: any) => {
          this.firstName = '';
          this.lastName = '';
          this.email = '';
          this.phone = '';
          this.role = '';
          this.showOther = false
          this.showAddContactModal = false;
          console.log("Saved contact:", this.contacts);
          console.log('Form Submitted', this.contactForm.value);
          // Reset form state
          this.contactForm.reset();
          this.submitted = false;
          console.log('Contact added successfully:', res);
        },
        error: (err) => {
          console.error('Error adding contact:', err);
        }
      });

    } else {
      console.log("Please fill all contact information :", this.contactForm.value)
    }
  }

  editContact(index: number): void {
    const selectedContact = this.contacts[index];
    console.log("selectedContact :", selectedContact);
    this.firstName = selectedContact.firstName;
    this.lastName = selectedContact.lastName;
    this.email = selectedContact.email;
    this.phone = selectedContact.phone;

    this.role = capitalizeFirst((selectedContact.role === "Accountant" || selectedContact.role === "Key Account Manager" || selectedContact.role === "Product Manager" || selectedContact.role === "Underwriter") ? selectedContact.role : 'Other')
    this.selectedIndex = index
    if (this.role === "Other") {
      this.contact_other = selectedContact.role;
      this.showOther = true;
    }
    this.showContactEditModal = true;
  }

  //for updating contact
  onUpdateContact(index: number, supplierId: number) {
    this.submitted = true;


    if (this.contactForm.invalid) {
      return; // Stop if validation fails
    }
    const contact = {
      id: this.contacts[index].id,
      firstName: capitalizeFirst(this.firstName),
      lastName: capitalizeFirst(this.lastName),
      email: toLowerCase(this.email),
      phone: this.phone,
      role: this.role === 'Other' ? capitalizeFirst(this.contact_other) : capitalizeFirst(this.role)
    }
    const contactId = contact.id
    // API CALL to update contact
    const url = `${environment.apiUrl}/api/ap/suppliers/update-contact/${supplierId}/${contactId}`;

    this.http.patch(url, { contactData: contact }).subscribe({
      next: (res: any) => {
        this.contacts[index] = {
          firstName: capitalizeFirst(this.firstName),
          lastName: capitalizeFirst(this.lastName),
          email: toLowerCase(this.email),
          phone: this.phone,
          role: this.role === 'Other' ? this.contact_other : capitalizeFirst(this.role)
        };
        console.log('Contact updated successfully:', res);
        // Reset form and state
        this.showContactEditModal = false;
        this.showContactModal = false;
        this.getSuppliers();
        this.firstName = '';
        this.lastName = '';
        this.email = '';
        this.phone = '';
        this.role = '';
        this.contact_other = '';
        this.showOther = false;
        this.submitted = false;
        this.showContactEditModal = false;
        this.contactForm.reset(); // Reset reactive form too
      },
      error: (err) => {
        console.error('Error updating contact:', err);
      }
    });
  }

  // close update contact popup
  onCloseEditPopup() {
    this.firstName = '';
    this.lastName = '';
    this.email = '';
    this.phone = '';
    this.role = ''
    this.contact_other = ''
    this.showOther = false;


    this.showContactEditModal = false;
    // this.showContactModal = false;
    this.contactForm.reset();
    this.submitted = false;
  }
  onCloseAddContactPopup() {
    this.firstName = '';
    this.lastName = '';
    this.email = '';
    this.phone = '';
    this.role = ''
    this.contact_other = ''
    this.showOther = false;
    // this.showContactEditModal = false;
    this.showAddContactModal = false;
    this.contactForm.reset();
    this.submitted = false;
  }


  addNewContact(): void {
    this.showAddContactModal = true;
  }



  // ************************** Handling Product Section ****************************************
  products: any[] = [];
  selectedProduct: any = {};
  showProductModal: boolean = false;
  showSingleProductModal: boolean = false;
  productModalMode: 'view' | 'edit' | null = null;
  baseUnitValues = [
    { id: 1, value: '$1' },
    { id: 2, value: '$100' },
    { id: 3, value: '$1000' },
    { id: 4, value: '$10000' },
    { id: 5, value: '$100000' },
    { id: 6, value: '$1000000' }
  ];
  // my first row variable
  // country setting
  countries = [
    { id: 2, name: 'Canada' },
    { id: 1, name: 'USA' }];

  countryDropdownSettings = {
    singleSelection: true,
    idField: 'id',
    textField: 'name',
    allowSearchFilter: false,
    closeDropDownOnSelection: true
  };

  filteredProvinces: any[] = [];
  provinces = [
    { id: 1, name: 'Alberta', shortName: 'AB' },
    { id: 2, name: 'British Columbia', shortName: 'BC' },
    { id: 3, name: 'Manitoba', shortName: 'MB' },
    { id: 4, name: 'New Brunswick', shortName: 'NB' },
    { id: 5, name: 'Newfoundland and Labrador', shortName: 'NL' },
    { id: 6, name: 'Nova Scotia', shortName: 'NS' },
    { id: 7, name: 'Ontario', shortName: 'ON' },
    { id: 8, name: 'Prince Edward Island', shortName: 'PE' },
    { id: 9, name: 'Quebec', shortName: 'QC' },
    { id: 10, name: 'Saskatchewan', shortName: 'SK' },
    { id: 11, name: 'Northwest Territories', shortName: 'NT' },
    { id: 12, name: 'Nunavut', shortName: 'NU' },
    { id: 13, name: 'Yukon', shortName: 'YT' }
  ];

  dropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'shortName',
    itemsShowLimit: 2,
    selectAllText: 'Select All',
    unSelectAllText: 'Unselect All',
    enableCheckAll: true,
    allowSearchFilter: true
  };

  //show contact list popup
  openContactList(contacts: any[], supplierId: number): void {
    this.contacts = contacts;
    this.selectedSupplierId = supplierId;
    this.showAddContactModal = false;
    this.showContactModal = true;
  }
  selectedSupplierId: number = null;
  //show product list popup
  openProductList(products: any[], supplierId: number): void {
    this.products = products;
    this.selectedSupplierId = supplierId;
    this.showProductModal = true;
    console.log("products selected from perticular supplier :", products)
  }

  // editProduct 
  openSingleProductModal(product: any, mode: 'view' | 'edit'): void {
    this.selectedProduct = { ...product };
    this.productModalMode = mode;
    this.showSingleProductModal = true;
    console.log("product :", product);
  }

  //my first row functions code
  onItemSelect(item: any) {
    this.updateFilteredProvinces();
    console.log('Selected:', item);
  }


  onSelectAll(items: any) {
    console.log('All Selected:', items);
    // this.updateFilteredProvinces();
    // this.filteredProvinces = [...items];
  }


  onDeSelectAll(items: any) {
    // this.filteredProvinces = [];
  }

  onItemDeSelect(item: any) {
    // this.updateFilteredProvinces();
  }
  //here we are filtering provinces to show in pricing area section dropdown
  updateFilteredProvinces() {
    // this.filteredProvinces = this.selectedProvinces;
  }

  //increase table rows 
  generatePricingAreaRows(count: number) {
    this.selectedProduct.areas.areasData = [];
    for (let i = 0; i < count; i++) {
      this.selectedProduct.areas.areasData.push({
        area: i + 1,
        name: '',
        selectedProvinces: []
      });
    }
  }
  // Filtered list per row
  getAvailableProvinces(index: number) {
    const selectedInOthers = this.selectedProduct.areas.areasData
      .filter((_, i) => i !== index)
      .flatMap(r => r.selectedProvinces.map(p => p.id));

    return this.filteredProvinces.filter(
      p => !selectedInOthers.includes(p.id)
    );
  }
  //pricing provinces setting
  pricingProvincesdropdownSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'shortName',
    itemsShowLimit: 1,
    allowSearchFilter: true,
    enableCheckAll: true,
    closeDropDownOnSelection: false
  };

  onAgeBandChange(value: boolean): void {
    this.selectedProduct.ageBands.isAgeBandedEnabled = value;
    if (value) {
      this.selectedProduct.tiers.isTierEnabled = false;
      this.selectedProduct.tiers.noOfTiers = null
      this.selectedProduct.tiers.tiersData = [];
    } else {
      this.selectedProduct.ageBands.ageBandsData = [];
      this.selectedProduct.ageBands.noOfAgeBands = null;
    }
  }
  onTierChange(value: boolean): void {
    this.selectedProduct.tiers.isTierEnabled = value;

    if (value) {
      this.selectedProduct.ageBands.isAgeBandedEnabled = false;
      this.selectedProduct.ageBands.noOfAgeBands = null;
      this.selectedProduct.ageBands.ageBandsData = [];
    } else {
      this.selectedProduct.tiers.tiersData = [];
      this.selectedProduct.tiers.noOfTiers = null;
    }
  }
  onVarientChange(value: boolean): void {
    this.selectedProduct.variants.isVariantEnabled = value;
    this.selectedProduct.variants.noOfVariants = null;
    this.selectedProduct.variants.variantsData = [];
  }
  onServiceLevelChange(value: boolean): void {
    this.selectedProduct.serviceLevel.isServiceLevelEnabled = value;
    this.selectedProduct.serviceLevel.noOfServiceLevel = null;
    this.selectedProduct.serviceLevel.servicesData = [];
  }
  // close product list popup
  closeModal(): void {
    this.showProductModal = false;
    this.showContactModal = false;
  }
  // 🔽 Initial lifecycle method
  ngOnInit(): void {
    this.getLanguageTokens();

    // 🔽 DataTable Options
    this.dtOptions = {
      pagingType: 'simple_numbers',
      order: [],
      pageLength: 10,
      language: {
        searchPlaceholder: 'Search here...'
      },
      destroy: true,
    };

    this.getSuppliers();

    // VALIDATION FOR CONTACT FORM
    this.contactForm = this.formBuilder.group({
      firstName: ['', RxwebValidators.required()],
      lastName: ['', RxwebValidators.required()],
      email: ['', [RxwebValidators.required(), RxwebValidators.email()]],
      phone: [
        '',
        [
          RxwebValidators.required(),
          RxwebValidators.pattern({ expression: { exactTen: /^.{10}$/ } })
        ]
      ]
      ,
      role: ['', RxwebValidators.required()],
      contact_other: ['']
    });
  }

  // 🌐 Handle Language Tokens
  public getLanguageTokens(): void {
    this.languageData$ = this.store.select(state => state.languageData).pipe(
      startWith([]),
    );

    this.languageData$.subscribe((data) => {
      if (
        !data ||
        !data.languageData ||
        Object.keys(data.languageData).length <= 0
      ) {
        console.log('No data to process', new Date().toISOString());
        return;
      }

      setTimeout(() => {
        this.lang = {};
        this.selectedLanguage = sessionStorage.getItem('language') ?? 'en';

        for (let token of data.languageData) {
          this.lang[token.key] = token[this.selectedLanguage];
        }
      }, 0);
    });
  }

  // 🔚 Unsubscribe to prevent memory leaks
  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
  }
}
